import { HttpStatusCode, type AxiosResponse } from "axios";
import { byIdsResultJSONZ, type ByIdsResultJSON, type UnavailableIdJSON } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import { readDownloadErrorBody } from "documents/_api/document-download-failure";
import BulkMessageAPIRequest from "messaging/_api/commands/bulk-message-api-command";
import CreateMessageAPIRequest from "messaging/_api/commands/create-message-api-command";
import MessageQuery from "messaging/_api/queries/message-query";
import {
  BulkMessageResponseZ,
  bulkMessageIssues,
  isBulkMessageSuccess,
} from "messaging/_schemas/bulk-message-response-schema";
import MessageSchema from "messaging/_schemas/message-schema";
import MessageHistorySchema from "messaging/_schemas/message-history-schema";
import AttachmentQuery from "messaging/attachments/_api/queries/attachment-query";
import MessageFileInfoSchema from "messaging/attachments/_schemas/message-file-info-schema";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ForumQuery from "messaging/forums/_api/queries/forum-query";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import MarketplaceSubscriberInfoAPIResponse from "messaging/forums/subscribers/_api/responses/marketplace-subscriber-info-api-response";
import ForumParticipant from "messaging/forums/subscribers/forum-participant";
import ForumSubscriberInvitationCommand from "messaging/forums/subscribers/invitations/_apis/commands/forum-subscriber-invitation-api-command";
import Message, { inReadingOrder } from "messaging/message";
import MessageHistory from "messaging/message-history";
import z from "zod";
import { validateGuid, validateMessage, validateTopic } from "messaging/_api/messaging-validation";

const JSON_HEADERS = { "Content-Type": "application/json" };

/**
 * A thread's messages, unread. This schema checks only the envelope. `Message.fromJSONArray` reads
 * each message against the message schema on its own, so one malformed message is left out of its
 * thread instead of failing every thread in the answer (#6592).
 */
const MESSAGE_ROWS_Z = z.array(z.unknown());

/** Wire shape of one entry from `POST messaging/forums/messages/by-ids`: a forum id and its messages. */
const FORUM_MESSAGES_RESPONSE_Z = z.object({
  forumId: z.string(),
  messages: MESSAGE_ROWS_Z,
});

/**
 * Wire shape of `POST messaging/forums/messages/by-ids`: a thread page for every forum the reader may
 * read, and, for every other forum asked for, whether it is `missing` or `unauthorized`.
 */
const FORUMS_MESSAGES_BY_IDS_RESULT_Z = byIdsResultJSONZ(FORUM_MESSAGES_RESPONSE_Z);

/**
 * Wire shape of `POST messaging/forums/by-ids`: the forums the reader may read, and, for every other
 * forum asked for, whether it is `missing` or `unauthorized`.
 */
const FORUMS_BY_IDS_RESULT_Z = byIdsResultJSONZ(ForumSchema.ForumJSONZ);
export type ForumsByIdsResultJSON = ByIdsResultJSON<ForumJSON>;

/**
 * Wire shape of `GET messaging/proposals/{negotiationId}/comment-conversations`: one of the reader's two
 * conversations in a negotiation, and whether they read it (#6631). `unauthorized` is a conversation the
 * reader is refused; `missing` is one nobody ever opened.
 */
const PROPOSAL_COMMENT_CONVERSATIONS_Z = z.array(
  z.object({
    audience: z.string(),
    isExternal: z.boolean(),
    availability: z.enum(["available", "unauthorized", "missing"]),
    forumId: z.uuid().nullish(),
  }),
);
export type ProposalCommentConversationJSON = z.infer<typeof PROPOSAL_COMMENT_CONVERSATIONS_Z>[number];

/** Each readable forum's messages by forum id, and why every other requested forum is not there. */
export type MessagesByForumsResult = {
  messagesByForumId: Record<string, Message[]>;
  unavailable: UnavailableIdJSON[];
};

/** Wire shape of `POST messaging/forums/{forumId}/read`. */
const FORUM_READ_RESPONSE_Z = z.object({
  forumId: z.guid(),
  markedRead: z.number(),
  numUnreadMessages: z.number(),
});

/** The answer to marking a forum read: what it marked, and the reader's count after it. */
export type ForumReadResult = z.infer<typeof FORUM_READ_RESPONSE_Z>;

export type ForumStoreTarget =
  | { kind: "byTopic"; topicKey: string }
  | { kind: "byQuery"; queryKey: string }
  | { kind: "commentThreadsByProposal"; proposalId: string };

type MessageListArgs = {
  forumId: string;
  query: MessageQuery;
  append: boolean;
};

type MessageListByForumsArgs = {
  forumIds: Array<Guid | string>;
  query: MessageQuery;
};

type MessageIdArgs = {
  forumId: string;
  messageId: string;
};

type AttachmentIdArgs = {
  forumId: string;
  fileId: string;
};

type ForumListArgs = {
  query: ForumQuery;
  storeTarget?: ForumStoreTarget;
};

type ForumIdsArgs = {
  ids: Array<Guid | string>;
};

type ForumIdArgs = {
  forumId: string;
};

type ForumMutationArgs = {
  forum: Forum;
  /** Stable across a user retry so the server can resume an unknown-result operation. */
  operationId: Guid | string;
};

export type ForumOperation = {
  operationId: string;
  operationType: string;
  status: string;
  forumId: string | null;
  messagingStage: string;
  marketplaceStage: string;
  workStage: string;
  permissionsStage: string;
};

const FORUM_OPERATION_Z = z.object({
  operationId: z.uuid(),
  operationType: z.string(),
  status: z.string(),
  forumId: z.uuid().nullable(),
  messagingStage: z.string(),
  marketplaceStage: z.string(),
  workStage: z.string(),
  permissionsStage: z.string(),
});

type ForumSubscriberInfoArgs = {
  forumId: string;
};

type InviteSubscriberToForumArgs = {
  forumId: string;
  inviteeId: Guid | string;
  invitationMessage?: string;
};

type AcceptForumInvitationArgs = {
  invitationId: string;
};

type SharedFilesArgs = {
  /** Every forum open in the chat window. The list is theirs together, in one order. */
  forumIds: string[];
  query: AttachmentQuery;
  append: boolean;
};

/** What every list of unread conversations provides, so a message that arrives can refresh them alone. */
export const unreadForumsTag = { type: "Forum" as const, id: "UNREAD" };
/** What every page of a conversation's messages provides. */
export const messageListTag = { type: "Message" as const, id: "LIST" };
/** What every page of the Shared files lists provides. */
export const sharedFilesTag = { type: "MessageAttachment" as const, id: "LIST" };

export const messagingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getForums: build.query<ForumJSON[], ForumListArgs>({
      query: ({ query }) => ({
        url: "messaging/forums",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumJSON[] =>
        ForumSchema.validateJSONArray(z.array(ForumSchema.ForumJSONZ).parse(response)),
      // A list of unread conversations changes whenever a message arrives, which the realtime
      // middleware announces with the Unread tag rather than refetching every list of forums.
      providesTags: (_result, _error, { query }) =>
        query.filterParameters.unread ? [{ type: "Forum", id: "LIST" }, unreadForumsTag] : [{ type: "Forum", id: "LIST" }],
    }),
    /**
     * How many conversations the reader has something unread in: the Unread chip's count (#6583).
     * The server totals them in X-Pagination; `pageSize=0` asks for the total and no rows, as the
     * Proposals chips do.
     */
    getUnreadForumCount: build.query<number, void>({
      query: () => ({
        url: "messaging/forums",
        params: new URLSearchParams({ unread: "true", pageSize: "0" }),
      }),
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (_response, meta): number => {
        const pagination = new URLSearchParams(meta?.response?.headers.get("x-pagination") ?? "");
        return Number.parseInt(pagination.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "Forum", id: "LIST" }, unreadForumsTag],
    }),
    getForumsByIds: build.query<ForumsByIdsResultJSON, ForumIdsArgs>({
      query: ({ ids }) => {
        if (ids.length === 0) throw new Error("At least one forum is required.");
        for (const id of ids) {
          const error = validateGuid(id, "Forum");
          if (error) throw new Error(error);
        }
        return ({
        url: "messaging/forums/by-ids",
        method: "POST",
        body: JSON.stringify(ids.map(toGuidValue)),
        headers: JSON_HEADERS,
        });
      },
      rawResponseSchema: FORUMS_BY_IDS_RESULT_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumsByIdsResultJSON => {
        const { found, unavailable } = FORUMS_BY_IDS_RESULT_Z.parse(response);
        return { found: ForumSchema.validateJSONArray(found), unavailable };
      },
      providesTags: [{ type: "Forum", id: "LIST" }],
    }),
    getProposalCommentConversations: build.query<ProposalCommentConversationJSON[], { negotiationId: string }>({
      query: ({ negotiationId }) => `messaging/proposals/${negotiationId}/comment-conversations`,
      rawResponseSchema: PROPOSAL_COMMENT_CONVERSATIONS_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown) => PROPOSAL_COMMENT_CONVERSATIONS_Z.parse(response),
      providesTags: [{ type: "Forum", id: "LIST" }],
    }),
    createForum: build.mutation<ForumJSON, ForumMutationArgs>({
      query: ({ forum, operationId }) => {
        const topicError = validateTopic(forum.topic);
        if (topicError) throw new Error(topicError);
        return {
        url: "messaging/forums",
        method: "POST",
        body: JSON.stringify({ ...forum.toJSON(), operationId: toGuidValue(operationId) }),
        headers: JSON_HEADERS,
        };
      },
      rawResponseSchema: ForumSchema.ForumJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumJSON => ForumSchema.validateJSON(response),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    deleteForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}`,
        method: "DELETE",
      }),
      onQueryStarted: async ({ forumId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // The forum is gone, which is what the API now answers for it too.
          dispatch(
            messagingApi.util.upsertQueryData("getForumsByIds", { ids: [forumId] }, {
              found: [],
              unavailable: [{ id: forumId, reason: "missing" }],
            }),
          );
        } catch {
          // Failed deletes should leave existing forum-by-id query data intact.
        }
      },
    }),
    resolveForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}/resolve`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    /**
     * Marks every message in the forum that the reader has still to read as read: loaded on this
     * screen or not (#5828). `readerId` stays on this side. The server marks the caller's messages, and
     * the stores use it to draw the loaded rows read without fetching them again.
     */
    markForumRead: build.mutation<ForumReadResult, { forumId: string; readerId: Guid }>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}/read`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: FORUM_READ_RESPONSE_Z,
      onSchemaFailure: logSchemaFailure,
      // Other lists of forums, such as the dashboard's conversations card, carry the count too.
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    reopenForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}/reopen`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    updateForumSubscribers: build.mutation<void, ForumMutationArgs>({
      query: ({ forum, operationId }) => {
        if (!forum.id) {
          throw new Error("Forum must have an id to update subscribers");
        }
        return {
          url: `messaging/forums/${forum.id.value}/subscribers`,
          method: "PUT",
          body: JSON.stringify({
            operationId: toGuidValue(operationId),
            subscribers:
              forum.subscribers?.map((subscriber) => ({
                userId: subscriber.userId,
                legalEntityId: subscriber.legalEntityId,
              })) ?? [],
          }),
          headers: JSON_HEADERS,
        };
      },
      invalidatesTags: (_result, _error, { forum: _forum }) => [
        { type: "Forum", id: "LIST" },
        { type: "ForumSubscriber", id: "LIST" },
      ],
    }),
    getForumOperation: build.query<ForumOperation, { operationId: Guid | string }>({
      query: ({ operationId }) => `messaging/operations/${toGuidValue(operationId)}`,
      rawResponseSchema: FORUM_OPERATION_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumOperation => FORUM_OPERATION_Z.parse(response),
    }),
    getForumSubscriberInfo: build.query<ForumParticipant[], ForumSubscriberInfoArgs>({
      query: ({ forumId }) => `messaging/forums/${forumId}/subscribers`,
      transformResponse: (response: unknown): ForumParticipant[] => {
        if (!Array.isArray(response)) return [];
        return response
          .map((info: Partial<MarketplaceSubscriberInfoAPIResponse>) =>
            Object.assign(new MarketplaceSubscriberInfoAPIResponse({}), info),
          )
          .map((info) => new ForumParticipant(info.deserialize(), info.hasLeft ?? false));
      },
      providesTags: [{ type: "ForumSubscriber", id: "LIST" }],
    }),
    inviteSubscriberToForum: build.mutation<void, InviteSubscriberToForumArgs>({
      query: ({ forumId, inviteeId, invitationMessage }) => {
        const forumError = validateGuid(forumId, "Forum");
        const inviteeError = validateGuid(inviteeId, "Invitee");
        if (forumError || inviteeError) throw new Error(forumError ?? inviteeError);
        if (invitationMessage !== undefined && invitationMessage.trim().length === 0) {
          throw new Error("Invitation message cannot be blank.");
        }
        return {
        url: `messaging/forums/${forumId}/invite-subscriber`,
        method: "POST",
        body: JSON.stringify(new ForumSubscriberInvitationCommand(toGuid(inviteeId), invitationMessage)),
        headers: JSON_HEADERS,
        };
      },
      invalidatesTags: (_result, _error, { forumId: _forumId }) => [
        { type: "ForumSubscriber", id: "LIST" },
        { type: "ForumInvitation", id: "LIST" },
      ],
    }),
    acceptForumInvitation: build.mutation<void, AcceptForumInvitationArgs>({
      query: ({ invitationId }) => {
        const error = validateGuid(invitationId, "Invitation");
        if (error) throw new Error(error);
        return ({
        url: `messaging/forum-invitations/${invitationId}/accept`,
        method: "POST",
        body: {},
        });
      },
      invalidatesTags: (_result, _error, { invitationId }) => [
        { type: "ForumInvitation", id: invitationId },
        { type: "ForumInvitation", id: "LIST" },
      ],
    }),
    getAttachmentsByForums: build.query<PaginatedResponse<MessageFileInfo>, SharedFilesArgs>({
      query: ({ forumIds, query }) => {
        const params = new URLSearchParams(forumIds.map((forumId) => ["forumIds", forumId]));
        query.searchParams.forEach((value, key) => params.append(key, value));
        return {
          url: "messaging/attachments",
          params,
        };
      },
      rawResponseSchema: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown, meta): PaginatedResponse<MessageFileInfo> => {
        const fileInfoJSON = z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).parse(response);
        const attachments = MessageFileInfo.fromJSONArray(fileInfoJSON);
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from<MessageFileInfo>(attachments, headers);
      },
      providesTags: [sharedFilesTag],
    }),
    getMessagesByForum: build.query<PaginatedResponse<Message>, MessageListArgs>({
      query: ({ forumId, query }) => ({
        url: `messaging/forums/${forumId}/messages`,
        params: query.searchParams,
      }),
      rawResponseSchema: MESSAGE_ROWS_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown, meta, { query }): PaginatedResponse<Message> => {
        const messageJSON = MESSAGE_ROWS_Z.parse(response);
        const messages = Message.fromJSONArray({ jsonArray: messageJSON });
        // A newest-first page arrives newest first, and every reader of the list draws it oldest first. It is
        // sorted rather than reversed: a server that does not know newestFirst answers oldest first, and a
        // reversal turned that conversation upside down.
        if (query.messageParameters.filterParameters.newestFirst) messages.sort(inReadingOrder);
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from<Message>(messages, headers);
      },
      providesTags: [messageListTag],
    }),
    /**
     * The batch sibling of `getMessagesByForum`: one request for every thread's messages instead of one
     * per forum, so a proposal's comment-thread pre-load stops paying an N+1. Keyed by forum id (a
     * `CommentThread`'s own id) rather than returned as a flat list, since a forum with no messages
     * still needs an empty entry to distinguish "loaded, nothing there" from "not requested." A forum
     * that is not readable is not keyed at all; `unavailable` says whether it is missing or refused.
     */
    getMessagesByForums: build.query<MessagesByForumsResult, MessageListByForumsArgs>({
      query: ({ forumIds, query }) => {
        if (forumIds.length === 0) throw new Error("At least one forum is required.");
        for (const forumId of forumIds) {
          const error = validateGuid(forumId, "Forum");
          if (error) throw new Error(error);
        }
        return ({
        url: "messaging/forums/messages/by-ids",
        method: "POST",
        params: query.searchParams,
        body: JSON.stringify(forumIds.map(toGuidValue)),
        headers: JSON_HEADERS,
        });
      },
      rawResponseSchema: FORUMS_MESSAGES_BY_IDS_RESULT_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown, _meta, { query }): MessagesByForumsResult => {
        const { found, unavailable } = FORUMS_MESSAGES_BY_IDS_RESULT_Z.parse(response);
        const newestFirst = query.messageParameters.filterParameters.newestFirst;
        const messagesByForumId: Record<string, Message[]> = {};
        for (const entry of found) {
          const messages = Message.fromJSONArray({ jsonArray: entry.messages });
          // A newest-first page arrives newest first, and is sorted as `getMessagesByForum`'s is (#6769).
          if (newestFirst) messages.sort(inReadingOrder);
          messagesByForumId[entry.forumId] = messages;
        }
        return { messagesByForumId, unavailable };
      },
      providesTags: [{ type: "Message", id: "LIST" }],
    }),
    createMessage: build.mutation<Message, { message: Message }>({
      query: ({ message }) => {
        const errors = validateMessage(message);
        if (Object.keys(errors).length > 0) throw new Error(Object.values(errors)[0]);
        const forumId = message.forum.id;
        if (!forumId) throw new Error("Message must have a forum id to be created");
        const request = new CreateMessageAPIRequest(message);
        return {
          url: `messaging/forums/${forumId.value}/messages`,
          method: "POST",
          body: request.formData,
        };
      },
      onQueryStarted: async ({ message }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          // A conflict response means the authorized forum changed. Refresh once so the user can decide what
          // to do with the stale draft; never replay the multipart write.
          const rejected = typeof error === "object" && error !== null && "error" in error
            ? (error as { error?: unknown }).error
            : error;
          const status = typeof rejected === "object" && rejected !== null && "status" in rejected
            ? (rejected as { status?: unknown }).status
            : undefined;
          if (status === HttpStatusCode.Conflict && message.forum.id) {
            const request = dispatch(messagingApi.endpoints.getForumsByIds.initiate(
              { ids: [message.forum.id] },
              { forceRefetch: true, subscribe: false },
            ));
            await request.unwrap().catch(() => undefined);
            request.unsubscribe();
          }
        }
      },
      rawResponseSchema: MessageSchema.MessageJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): Message => {
        const params = MessageSchema.deserialize(response);
        return new Message(params);
      },
    }),
    /**
     * Rewrites the text of a message its author sent. The server refuses anybody else, and an author who
     * no longer writes in the forum, with a 403; it keeps the earlier text as the message's history and
     * broadcasts the edit to the forum's readers as `message-updated`.
     */
    updateMessage: build.mutation<Message, { messageId: Guid | string; content: string }>({
      query: ({ messageId, content }) => ({
        url: `messaging/${toGuidValue(messageId)}`,
        method: "PATCH",
        body: JSON.stringify({ id: toGuidValue(messageId), contentPlainText: content }),
        headers: JSON_HEADERS,
      }),
      rawResponseSchema: MessageSchema.MessageJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): Message => new Message(MessageSchema.deserialize(response)),
    }),
    /**
     * Deletes a message its author sent. The server refuses anybody else with a 403, and broadcasts the
     * tombstone to the forum's readers.
     */
    deleteMessage: build.mutation<Message, { messageId: Guid | string }>({
      query: ({ messageId }) => ({
        url: `messaging/${toGuidValue(messageId)}`,
        method: "DELETE",
      }),
      rawResponseSchema: MessageSchema.MessageJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): Message => new Message(MessageSchema.deserialize(response)),
    }),
    createBulkMessages: build.mutation<Message[], { messages: Message[] }>({
      async queryFn({ messages }, _api, _extraOptions, fetchWithBQ) {
        if (!messages.length) {
          console.warn("No messages to create");
          return { data: [] };
        }

        let request: BulkMessageAPIRequest;
        try {
          request = new BulkMessageAPIRequest(messages);
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: error instanceof Error ? error.message : "Invalid message batch." } };
        }
        const result = await fetchWithBQ({
          url: "messaging/bulk-messages",
          method: "POST",
          body: request.payload,
          headers: JSON_HEADERS,
        });

        if (result.error) return { error: result.error };

        // The batch answers 200 even when individual messages failed, so the outcome is per entry. The
        // envelope is checked here; each saved message is read on its own by fromJSONArray (#6592).
        // Both the parse failure and the reported-issue case return an error rather than throwing:
        // this runs inside a queryFn, where a throw escapes as an unhandled rejection instead of a
        // failed mutation, which is how a save could fail forever without ever saying why.
        const parsed = z.array(BulkMessageResponseZ).safeParse(result.data);
        if (!parsed.success) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "The server returned an unrecognized response while saving messages.",
            },
          };
        }

        const issues = bulkMessageIssues(parsed.data);
        if (issues.length > 0) {
          return {
            error: { status: "CUSTOM_ERROR", error: issues.join(" "), data: issues },
          };
        }

        const messageJSON = parsed.data.filter((entry) => isBulkMessageSuccess(entry));
        return { data: Message.fromJSONArray({ jsonArray: messageJSON }) };
      },
      // A forum carries its reader's unread count, and this batch is the only way the app changes
      // one: `markRead` and `markUnread` both travel here. Invalidating messages alone left every
      // list of forums holding the count it fetched before the read, which the dashboard's
      // conversations card showed as a number that would not move (#5366).
      invalidatesTags: (_result, _error, { messages: _messages }) => [
        { type: "Message", id: "LIST" },
        { type: "Forum", id: "LIST" },
      ],
    }),
    getMessageImages: build.query<MessageFileInfo[] | null, MessageIdArgs>({
      query: ({ forumId, messageId }) => `messaging/forums/${forumId}/messages/${messageId}/images`,
      rawResponseSchema: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).nullable(),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): MessageFileInfo[] | null => {
        if (response === null) return null;
        const imageJSON = z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).parse(response);
        return MessageFileInfo.fromJSONArray(imageJSON);
      },
      providesTags: (_result, _error, { messageId }) => [{ type: "Message", id: messageId }],
    }),
    getMessageHistory: build.query<MessageHistory[], MessageIdArgs>({
      query: ({ forumId, messageId }) => `messaging/forums/${forumId}/messages/${messageId}/history`,
      rawResponseSchema: z.array(MessageHistorySchema.MessageHistoryJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): MessageHistory[] => {
        const historyJSON = z.array(MessageHistorySchema.MessageHistoryJSONZ).parse(response);
        return MessageHistory.fromJSONArray(historyJSON);
      },
      providesTags: (_result, _error, { messageId }) => [{ type: "Message", id: messageId }],
    }),
    downloadMessageAttachment: build.query<AxiosResponse<Blob>, AttachmentIdArgs>({
      query: ({ forumId, fileId }) => ({
        url: `messaging/forums/${forumId}/attachments/${fileId}`,
        responseHandler: async (response: Response) => {
          // A refusal carries its reason: the file is still being scanned, was found infected, or sits
          // in a forum the caller cannot read (#6876). Read as a blob, that reason never reaches the
          // error reader.
          if (!response.ok) return readDownloadErrorBody(response);

          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });

          return {
            data: await response.blob(),
            headers,
            request: {
              getResponseHeader: (name: string) => response.headers.get(name),
            },
          } as AxiosResponse<Blob>;
        },
      }),
      providesTags: (_result, _error, { forumId, fileId }) => [
        { type: "MessageAttachment", id: fileId },
        { type: "MessageAttachment", id: `FORUM-${forumId}` },
      ],
    }),
  }),
});

export const {
  useGetForumsQuery,
  useGetUnreadForumCountQuery,
  useLazyGetForumsQuery,
  useGetForumsByIdsQuery,
  useLazyGetForumsByIdsQuery,
  useGetProposalCommentConversationsQuery,
  useCreateForumMutation,
  useDeleteForumMutation,
  useResolveForumMutation,
  useReopenForumMutation,
  useUpdateForumSubscribersMutation,
  useLazyGetForumOperationQuery,
  useGetForumSubscriberInfoQuery,
  useLazyGetForumSubscriberInfoQuery,
  useInviteSubscriberToForumMutation,
  useAcceptForumInvitationMutation,
  useGetAttachmentsByForumsQuery,
  useLazyGetAttachmentsByForumsQuery,
  useGetMessagesByForumQuery,
  useLazyGetMessagesByForumQuery,
  useGetMessagesByForumsQuery,
  useLazyGetMessagesByForumsQuery,
  useCreateMessageMutation,
  useCreateBulkMessagesMutation,
  useUpdateMessageMutation,
  useDeleteMessageMutation,
  useGetMessageImagesQuery,
  useLazyGetMessageHistoryQuery,
  useLazyDownloadMessageAttachmentQuery,
} = messagingApi;

function toGuid(value: Guid | string): Guid {
  return value instanceof Guid ? value : new Guid(value);
}

function toGuidValue(value: Guid | string): string {
  return value instanceof Guid ? value.value : value;
}
